<script setup lang="ts">
import { shallowRef, watchEffect } from 'vue'
import { onKeyStroke } from '@vueuse/core'
import { isDark } from '../../helpers/utils'
import { VCloseIconButtonVariants } from '../VIconButton/types'
import type { IDrawerEvents, IDrawerProps, TDrawerConfig } from './types'
import { useDrawerConfig } from '@/ui/plushComponents/VDrawer/composables/useDrawerConfig'

const props = withDefaults(defineProps<IDrawerProps>(), {
    showHeader: false,
})
const emit = defineEmits<IDrawerEvents>()
const isOpen = defineModel<boolean>({
    default: false,
})

const drawerConfig = shallowRef({} as TDrawerConfig)

watchEffect(() => {
    drawerConfig.value = useDrawerConfig(props)
})

function close(): void {
    if (!props.nonDismissible) {
        isOpen.value = false
        emit('close')
    }
}

onKeyStroke('Escape', () => {
    if (isOpen.value) close()
})
</script>

<template>
    <Teleport to="#teleports">
        <div
            :class="[isDark(props.dark)]"
            role="dialog"
            :aria-model="isOpen"
            data-gpp-tools-ui
        >
            <VBlanket
                :dark="props.dark"
                :is-open="isOpen"
                :data-test="`blanket_${props.dataTest}`"
                @blanket-click="close"
            />

            <Transition name="drawer-transition">
                <div
                    v-if="isOpen"
                    :class="drawerConfig.root"
                >
                    <div :class="drawerConfig.header">
                        <div
                            v-if="!(nonDismissible || hideCloseButton)"
                            :class="drawerConfig.containerButton"
                        >
                            <VIconButton
                                :dark="props.dark"
                                :data-test="`iconButton_${props.dataTest}`"
                                icon="close"
                                :variant="VCloseIconButtonVariants.tertiary"
                                size="MEDIUM"
                                aria-label="zamknij sidebar"
                                class="md:size-2xl"
                                @click="close"
                            />
                        </div>

                        <div v-if="showHeader">
                            <div :class="drawerConfig.containerTitleDescription">
                                <div
                                    v-if="title"
                                    :class="drawerConfig.title"
                                >
                                    {{ title }}
                                </div>
                                <div
                                    v-if="description"
                                    :class="drawerConfig.description"
                                >
                                    {{ description }}
                                </div>
                            </div>

                            <VDivider v-if="title || description" />
                        </div>
                    </div>

                    <div :class="drawerConfig.content">
                        <slot />
                    </div>
                </div>
            </Transition>
        </div>
    </Teleport>
</template>

<style scoped>
.drawer-transition-enter-active{@apply transition-transform duration-500 ease-out}.drawer-transition-leave-active{@apply transition-transform duration-[400ms] ease-in}.drawer-transition-enter-from,.drawer-transition-leave-to{@apply max-md:translate-y-full md:translate-x-full}.drawer-transition-enter-to,.drawer-transition-leave-from{@apply max-md:translate-y-0 md:translate-x-0}
</style>
